export const FILE_SIZE_LIMITS = {
  IMAGE: 8 * 1024 * 1024,
  PDF: 40 * 1024 * 1024,
  VIDEO: 100 * 1024 * 1024
};

export const MIME_TYPES = {
  IMAGES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/avif'],
  VIDEOS: ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/webm'],
  PDF: ['application/pdf'],
  DOCUMENTS: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  SPREADSHEETS: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'],
  CAD: ['application/acad', 'application/x-acad', 'application/autocad_dwg', 'image/vnd.dwg', 'image/vnd.dxf'],
  COMPRESSED: ['application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed']
};

export const FILE_EXTENSIONS = {
  IMAGES: ['.jpg', '.jpeg', '.png', '.webp', '.avif'],
  VIDEOS: ['.mp4', '.mov', '.avi', '.wmv', '.webm'],
  PDF: ['.pdf'],
  DOCUMENTS: ['.pdf', '.doc', '.docx'],
  SPREADSHEETS: ['.xls', '.xlsx', '.csv'],
  CAD: ['.dwg', '.dxf'],
  COMPRESSED: ['.zip', '.rar', '.7z']
};

interface FileTypeConfig {
  accept: string;
  mimeTypes: string[];
  extensions: string[];
  maxSize: number;
  description: string;
}

export const TAG_FILE_CONFIG: Record<string, FileTypeConfig> = {
  'Carátula': {
    accept: '.jpg,.jpeg,.png,.webp,.avif,.mp4,.mov,.avi,.wmv,.webm',
    mimeTypes: [...MIME_TYPES.IMAGES, ...MIME_TYPES.VIDEOS],
    extensions: [...FILE_EXTENSIONS.IMAGES, ...FILE_EXTENSIONS.VIDEOS],
    maxSize: FILE_SIZE_LIMITS.IMAGE,
    description: 'JPG, PNG, WEBP, AVIF o video (MP4, MOV, AVI, WMV, WEBM)'
  },
  'Vista': {
    accept: '.jpg,.jpeg,.png,.webp,.avif,.pdf',
    mimeTypes: [...MIME_TYPES.IMAGES, ...MIME_TYPES.PDF],
    extensions: [...FILE_EXTENSIONS.IMAGES, ...FILE_EXTENSIONS.PDF],
    maxSize: FILE_SIZE_LIMITS.IMAGE,
    description: 'JPG, PNG, WEBP, AVIF o PDF'
  },
  'Plano': {
    accept: '.jpg,.jpeg,.png,.webp,.avif,.pdf,.dwg,.dxf',
    mimeTypes: [...MIME_TYPES.IMAGES, ...MIME_TYPES.PDF, ...MIME_TYPES.CAD],
    extensions: [...FILE_EXTENSIONS.IMAGES, ...FILE_EXTENSIONS.PDF, ...FILE_EXTENSIONS.CAD],
    maxSize: FILE_SIZE_LIMITS.PDF,
    description: 'JPG, PNG, WEBP, AVIF, PDF, DWG o DXF'
  },
  'Render exterior': {
    accept: '.jpg,.jpeg,.png,.webp,.avif,.pdf',
    mimeTypes: [...MIME_TYPES.IMAGES, ...MIME_TYPES.PDF],
    extensions: [...FILE_EXTENSIONS.IMAGES, ...FILE_EXTENSIONS.PDF],
    maxSize: FILE_SIZE_LIMITS.IMAGE,
    description: 'JPG, PNG, WEBP, AVIF o PDF'
  },
  'Render interior': {
    accept: '.jpg,.jpeg,.png,.webp,.avif,.pdf',
    mimeTypes: [...MIME_TYPES.IMAGES, ...MIME_TYPES.PDF],
    extensions: [...FILE_EXTENSIONS.IMAGES, ...FILE_EXTENSIONS.PDF],
    maxSize: FILE_SIZE_LIMITS.IMAGE,
    description: 'JPG, PNG, WEBP, AVIF o PDF'
  },
  'Memoria descriptiva': {
    accept: '.pdf,.doc,.docx',
    mimeTypes: MIME_TYPES.DOCUMENTS,
    extensions: FILE_EXTENSIONS.DOCUMENTS,
    maxSize: FILE_SIZE_LIMITS.PDF,
    description: 'PDF, DOC o DOCX'
  },
  'Cómputo métrico': {
    accept: '.pdf,.xls,.xlsx,.csv',
    mimeTypes: [...MIME_TYPES.PDF, ...MIME_TYPES.SPREADSHEETS],
    extensions: [...FILE_EXTENSIONS.PDF, ...FILE_EXTENSIONS.SPREADSHEETS],
    maxSize: FILE_SIZE_LIMITS.PDF,
    description: 'PDF, XLS, XLSX o CSV'
  },
  'Especificaciones técnicas': {
    accept: '.pdf,.doc,.docx',
    mimeTypes: MIME_TYPES.DOCUMENTS,
    extensions: FILE_EXTENSIONS.DOCUMENTS,
    maxSize: FILE_SIZE_LIMITS.PDF,
    description: 'PDF, DOC o DOCX'
  },
  'Presupuesto': {
    accept: '.pdf,.xls,.xlsx,.csv',
    mimeTypes: [...MIME_TYPES.PDF, ...MIME_TYPES.SPREADSHEETS],
    extensions: [...FILE_EXTENSIONS.PDF, ...FILE_EXTENSIONS.SPREADSHEETS],
    maxSize: FILE_SIZE_LIMITS.PDF,
    description: 'PDF, XLS, XLSX o CSV'
  },
  'Video recorrido': {
    accept: '.mp4,.mov,.avi,.wmv,.webm',
    mimeTypes: MIME_TYPES.VIDEOS,
    extensions: FILE_EXTENSIONS.VIDEOS,
    maxSize: FILE_SIZE_LIMITS.VIDEO,
    description: 'MP4, MOV, AVI, WMV o WEBM'
  },
  'Modelo 3D': {
    accept: '.zip,.rar,.7z',
    mimeTypes: MIME_TYPES.COMPRESSED,
    extensions: FILE_EXTENSIONS.COMPRESSED,
    maxSize: FILE_SIZE_LIMITS.PDF,
    description: 'ZIP, RAR o 7Z'
  }
};

export const DEFAULT_FILE_CONFIG: FileTypeConfig = {
  accept: '.jpg,.jpeg,.png,.webp,.avif,.pdf,.doc,.docx,.xls,.xlsx',
  mimeTypes: [...MIME_TYPES.IMAGES, ...MIME_TYPES.PDF, ...MIME_TYPES.DOCUMENTS, ...MIME_TYPES.SPREADSHEETS],
  extensions: [...FILE_EXTENSIONS.IMAGES, ...FILE_EXTENSIONS.PDF, ...FILE_EXTENSIONS.DOCUMENTS, ...FILE_EXTENSIONS.SPREADSHEETS],
  maxSize: FILE_SIZE_LIMITS.PDF,
  description: 'JPG, PNG, WEBP, AVIF, PDF o documentos de Office'
};

export const getFileConfig = (tagName: string): FileTypeConfig => {
  return TAG_FILE_CONFIG[tagName] || DEFAULT_FILE_CONFIG;
};

export const validateFileSize = (file: File, tagName: string): { valid: boolean; error?: string; suggestLink?: boolean } => {
  const config = getFileConfig(tagName);
  
  if (file.size > config.maxSize) {
    const maxSizeMB = (config.maxSize / (1024 * 1024)).toFixed(0);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `El archivo es muy pesado (${fileSizeMB} MB). El tamaño máximo es ${maxSizeMB} MB.`,
      suggestLink: true
    };
  }

  if (!config.mimeTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Tipo de archivo no permitido. Por favor selecciona: ${config.description}`,
      suggestLink: false
    };
  }

  return { valid: true };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

